import { ShotType } from './../../../models/shots/shotEnums';
import { Injectable } from '@angular/core';
import { ScoreResponse } from '../../../models';
import { ShotFrom } from '../../../models/shots/shotEnums';
import { ScoreStatus } from '../../../models/scores/scoreEnums';
import { LoggingService } from '../../shared/logging/logging.service';

@Injectable({
  providedIn: 'root'
})
export class ShotStatsService {

  constructor(private loggingService: LoggingService) { }

  calculateAllStats(score: ScoreResponse, par: number): void {
    if (!score) {
      this.loggingService.warn('Cannot calculate stats: score object is undefined.', 'ShotStatsService');
      return;
    }

    const calculatedScore = this.calculateScore(score);

    if (calculatedScore === undefined) {
      score.plusMinus = undefined;
    } else {
      this.calculatePlusMinus(score, par);
    }

    this.calculatePutts(score);
    this.calculateFairwayHit(score, par);
    this.calculateGreenInRegulation(score, par);
    this.calculateSandSave(score);
  }

  calculateScore(score: ScoreResponse): number | undefined {
    if (!score) {
      this.loggingService.warn('Cannot calculate score: score object is undefined.', 'ShotStatsService');
      return undefined;
    }

    const shots = score.shots ?? [];
    const completedShots = shots.filter(shot => shot.shotFrom !== undefined);

    if (completedShots.length === 0) {
      const existingScore = score.score as unknown;

      if (existingScore === undefined || existingScore === null) {
        score.score = undefined;
        return undefined;
      }

      if (typeof existingScore === 'number') {
        if (existingScore === 0) {
          score.score = undefined;
          return undefined;
        }

        return existingScore;
      }

      if (typeof existingScore === 'string') {
        const trimmed = existingScore.trim();

        if (!trimmed || trimmed === '0') {
          score.score = undefined;
          return undefined;
        }

        const numericValue = Number(trimmed);

        if (!Number.isNaN(numericValue)) {
          score.score = numericValue;
          return score.score;
        }

        return score.score;
      }

      return score.score;
    }

    score.score = completedShots.length;

    return score.score;
  }

  calculatePlusMinus(score: ScoreResponse | undefined, par: number): void {
    if (!score) {
      this.loggingService.warn('Cannot calculate plus/minus: score object is undefined.', 'ShotStatsService');
      return;
    }

    if (score.score === undefined || par === undefined || par === null) {
      score.plusMinus = undefined;
      return;
    }

    score.plusMinus = score.score - par;
  }

  calculatePutts(score: ScoreResponse | undefined): number {
    if (!score) {
      this.loggingService.warn('Cannot calculate putts: score object is undefined.', 'ShotStatsService');
      return 0;
    }

    if (!score.shots || score.shots.length === 0) {
      this.loggingService.warn('Cannot calculate putts: shots array is empty or undefined.', 'ShotStatsService');
      return 0;
    }

    const greenShots = score.shots!.filter(shot => shot.shotFrom === ShotFrom.Green);
    score.putts = greenShots.length;

    return score.putts ?? 0;
  }

  calculateFairwayHit(score: ScoreResponse | undefined, par: number): boolean {
    if (!score) {
      this.loggingService.warn('Cannot calculate fairway hit: score object is undefined.', 'ShotStatsService');
      return false;
    }

    if (!score.shots || score.shots.length === 0) {
      this.loggingService.warn('Cannot calculate fairway hit: shots array is empty or undefined.', 'ShotStatsService');

      score.fairway = false;

      return false;
    }

    if (par !== 3 &&
      score.shots!.length! > 1 && (
        score.shots![1].shotFrom === ShotFrom.Fairway ||
        score.shots![1].shotFrom === ShotFrom.Fringe ||
        score.shots![1].shotFrom === ShotFrom.Green)) {

      score.fairway = true;

    } else {
      score.fairway = false;
    }

    return score.fairway;
  }

  calculateGreenInRegulation(score: ScoreResponse | undefined, par: number): boolean {
    if (!score) {
      this.loggingService.warn('Cannot calculate GIR: score object is undefined.', 'ShotStatsService');
      return false;
    }

    if (!score.shots || score.shots.length === 0) {
      this.loggingService.warn('Cannot calculate GIR: shots array is empty or undefined.', 'ShotStatsService');

      score.gir = false;

      return false;
    }

    score.gir = false;

    if (score.statusCode !== ScoreStatus.Incomplete &&
      score.plusMinus! <= -2) {

      score.gir = true;

    } else {
      const firstGreenShot = score.shots.find(shot => shot.shotFrom === ShotFrom.Green);

      score.gir =
        firstGreenShot !== undefined &&
        firstGreenShot.number! < par;
    }

    return score.gir;
  }

  calculateSandSave(score: ScoreResponse | undefined): boolean {
    if (!score) {
      this.loggingService.warn('Cannot calculate sand save: score object is undefined.', 'ShotStatsService');
      return false;
    }

    if (!score.shots || score.shots.length === 0) {
      this.loggingService.warn('Cannot calculate sand save: shots array is empty or undefined.', 'ShotStatsService');

      score.sandSaveAttempted = false;
      score.sandSaveMade = false;

      return false;
    }

    const bunkerShots = score?.shots?.filter(shot => shot.shotFrom === ShotFrom.GreensideBunker);

    score.sandSaveAttempted = bunkerShots?.length! > 0;

    if (bunkerShots?.length! > 0) {
      // Use the last bunker shot to determine sand save success
      // This accounts for scenarios where multiple bunker shots occur
      // but the player still gets up and down from the final bunker position
      const lastBunkerShot = bunkerShots![bunkerShots!.length - 1];
      score.sandSaveMade =
        score.sandSaveAttempted &&
        lastBunkerShot.number! >= score.shots!.length - 1;
    } else {
      score.sandSaveAttempted = false;
      score.sandSaveMade = false;
    }

    return score.sandSaveMade ?? false;
  }
}
